import { filterBy } from "@progress/kendo-data-query";
import {
  ComboBox,
  ComboBoxFilterChangeEvent,
  ComboBoxFocusEvent,
  ComboBoxProps,
} from "@progress/kendo-react-dropdowns";
import { FieldWrapper } from "@progress/kendo-react-form";
import { Error, Hint } from "@progress/kendo-react-labels";
import * as React from "react";
import { useCallback } from "react";
import useDeepCompareEffect, {
  useDeepCompareMemoize,
} from "use-deep-compare-effect";
import type { SelectData } from "./FormDropDownList";
import FormLabel from "./FormLabel";
import IconHint from "./IconHint";
import { useControlledField, type FieldValidator } from "./useControlledField";

type ComboBoxItem = Record<string, unknown>;

export interface FormComboBoxProps
  extends Omit<
    ComboBoxProps,
    "name" | "value" | "onChange" | "valid" | "data"
  > {
  name: string;
  validator?: FieldValidator;
  label?: string;
  hint?: string;
  hintIcon?: boolean;
  optional?: boolean;
  showErrorOnPristine?: boolean;
  floatingLabel?: boolean;
  floatLabel?: string;
  data?: ComboBoxItem[];
  customLabel?: string;
}

export const FormComboBox: React.FC<FormComboBoxProps> = ({
  name,
  validator,
  label,
  hint,
  hintIcon = false,
  optional,
  showErrorOnPristine = false,
  floatingLabel,
  floatLabel,
  disabled,
  placeholder,
  data = [],
  textField = "name",
  dataItemKey = "id",
  customLabel = "Custom",
  loading,
  ...others
}) => {
  const { field, fieldState } = useControlledField({ name, validator });
  const showError =
    !!fieldState.error && (showErrorOnPristine || fieldState.isTouched);
  const showHint = !!hint;
  const inputId = name;
  const labelId = `${inputId}_label`;
  const hintId = showHint ? `${inputId}_hint` : "";
  const errorId = showError ? `${inputId}_error` : "";

  const newData: SelectData[] = React.useMemo(() => {
    return (data as ComboBoxItem[]).map(item => ({
      ...item,
      name: textField ? (item[textField] as string) : (item.name as string),
      id: (dataItemKey ? item[dataItemKey] : item.id) as SelectData["id"],
      title: item.title as string | undefined,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataItemKey, textField, useDeepCompareMemoize(data)]);

  const [dropDownData, setDropDownData] = React.useState<SelectData[]>([]);

  useDeepCompareEffect(() => {
    setDropDownData(newData);
  }, [newData]);

  const filterChange = (event: ComboBoxFilterChangeEvent) => {
    const { filter } = event;
    const items = filterBy([...newData], filter);
    if (
      filter.value.length > 0 &&
      !items.find(item => item.name === filter.value)
    ) {
      items.unshift({
        id: filter.value,
        name: `${filter.value}`,
        title: `${filter.value}`,
        custom: customLabel,
      });
    }
    setDropDownData(items);
  };

  const onComboBoxFocus = useCallback((event: ComboBoxFocusEvent) => {
    if (!event.target.state.opened) {
      event.target.setState({ opened: true });
    }
  }, []);

  return (
    <FieldWrapper>
      <FormLabel
        id={labelId}
        editorId={inputId}
        editorValid={!fieldState.error}
        editorDisabled={!!disabled}
        floating={floatingLabel}
        label={label}
        value={
          field.value && typeof field.value === "object"
            ? String(
                (field.value as Record<string, unknown>)[dataItemKey] ?? "",
              )
            : undefined
        }
        floatLabel={floatLabel}
        placeholder={placeholder ?? undefined}
        optional={optional}
      >
        <ComboBox
          {...others}
          name={name}
          value={field.value as ComboBoxItem | null | undefined}
          textField={textField}
          dataItemKey={dataItemKey}
          filterable
          allowCustom
          onFilterChange={filterChange}
          data={dropDownData}
          onChange={e => field.onChange(e.value)}
          loading={loading}
          id={inputId}
          valid={!fieldState.error}
          disabled={disabled}
          onFocus={onComboBoxFocus}
          onBlur={field.onBlur}
        />
        {showHint && !hintIcon && <Hint id={hintId}>{hint}</Hint>}
        {showHint && hintIcon && (
          <IconHint hint={hint} style={{ right: "3.125rem", top: "2rem" }} />
        )}
        {showError && <Error id={errorId}>{fieldState.error?.message}</Error>}
      </FormLabel>
    </FieldWrapper>
  );
};

export default FormComboBox;
